import { useState, useMemo, useEffect } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { StoreInList } from "src/hooks/useStoresSWR"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"

// APIレスポンス（camelCase）
type StoresResponse = {
  stores: StoreInList[]
  pagination: {
    currentPage: number
    limitValue: number
    count: number
    totalCount?: number
    totalPages?: number
  }
}

const PER_PAGE = 20
const LOAD_MORE_TARGET = 500 // 「もっと読む」で取得したい総数

export const usePdfStores = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [allStores, setAllStores] = useState<StoreInList[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const [loadMorePagesRemaining, setLoadMorePagesRemaining] = useState(0)

  const url = useMemo(() => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      per: PER_PAGE.toString(),
      sort: "id",
      order: "desc",
      with_label: "true",
    })
    return `${SMOOTH_LINE_API_URL}/base_api/smoother/stores?${params.toString()}`
  }, [currentPage])

  const { getWithToken } = useSmoothApi()

  const fetcher = async (): Promise<StoresResponse> => {
    const res = await getWithToken(url)
    const data = res.data as StoresResponse

    // currentPageはフロントエンドで管理
    data.pagination.currentPage = currentPage

    return data
  }

  const storesSwr = useSWR(url, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  const refresh = async () => {
    setIsRefreshing(true)
    setCurrentPage(1)
    setTotalPages(0)
    setAllStores([])
    setHasInitiallyLoaded(false)
    setLoadMorePagesRemaining(0)
    try {
      await storesSwr.mutate()
    } finally {
      setIsRefreshing(false)
    }
  }

  // データが取得できたら累積ストアリストを更新
  useEffect(() => {
    if (storesSwr.data) {
      const pagination = storesSwr.data.pagination
      // pagination.countから総ページ数を計算（初期ロード基準）
      const totalPagesFromCount = Math.ceil(pagination.count / PER_PAGE)
      setTotalPages(totalPagesFromCount)

      // currentPageがundefinedの場合は1として扱う
      const effectiveCurrentPage = pagination.currentPage || 1

      if (!hasInitiallyLoaded) {
        // 初回ロードの場合のみ置き換え
        setAllStores(storesSwr.data.stores)
        setHasInitiallyLoaded(true)
      } else {
        // それ以外は常に追加（重複チェック付き）
        setAllStores((prev) => {
          const existingIds = new Set(prev.map((store) => store.id))
          const newStores = storesSwr.data.stores.filter((store) => !existingIds.has(store.id))
          return [...prev, ...newStores]
        })
      }

      // loadMore連続実行の処理
      if (loadMorePagesRemaining > 0) {
        // まだ取得するページがある場合は次のページを取得
        if (storesSwr.data.stores.length > 0 && currentPage < totalPages) {
          setLoadMorePagesRemaining((prev) => prev - 1)
          setCurrentPage((prev) => prev + 1)
        } else {
          // データがない、または最後のページに到達した場合は終了
          setLoadMorePagesRemaining(0)
          setIsLoadingMore(false)
        }
      } else if (isLoadingMore) {
        // loadMoreの最後のページ
        setIsLoadingMore(false)
      }
    }
  }, [storesSwr.data, currentPage, isLoadingMore, allStores.length])

  const loadMore = () => {
    if (currentPage < totalPages) {
      setIsLoadingMore(true)
      // 500件取得するために必要なページ数を計算（最大25ページ）
      const targetPages = Math.ceil(LOAD_MORE_TARGET / PER_PAGE)
      const remainingPages = totalPages - currentPage
      const pagesToLoad = Math.min(targetPages, remainingPages)

      setLoadMorePagesRemaining(pagesToLoad - 1) // 最初の1ページ分は次の行で実行されるので-1
      setCurrentPage((prev) => prev + 1)
    }
  }

  const hasMore = currentPage < totalPages

  return {
    stores: allStores,
    loading: !hasInitiallyLoaded && !storesSwr.data && !storesSwr.error && !isRefreshing,
    error: storesSwr.error,
    loadMore,
    hasMore,
    refresh,
    isRefreshing,
  }
}
